"use client";

import styles from "./SwitchButton.module.scss";

import { Button } from "@/dls/Button";

import { useControllableState } from "@/hooks/useControllableState";
import { cn } from "@/utils/cn";

type ExcludedProps = "children" | "prefix" | "suffix";
type ButtonProps = React.ComponentProps<typeof Button>;
type BaseProps = Omit<ButtonProps, ExcludedProps>;
type Element =
  | React.ReactNode
  | ((props: { checked: boolean }) => React.ReactNode);

interface Props extends BaseProps {
  prefix?: Element;
  suffix?: Element;
  children?: Element;
  checked?: boolean;
  defaultChecked?: boolean;
  shouldRemovePadding?: boolean;
  shouldPreventDefault?: boolean;
  shouldStopPropagation?: boolean;
  onCheckedChange?: (active: boolean) => void;
}

const render = (Element: Element, checked: boolean): React.ReactNode => {
  if (typeof Element === "function") {
    // Call render props directly: mounting an inline function as a component
    // gives it a new identity every render, which remounts its DOM nodes and
    // makes a surrounding focus trap (Dialog) steal focus mid-Tab.
    return Element({ checked });
  }
  return Element;
};

const SwitchButton: React.FC<Props> = ({
  className,
  type: htmlType = "button",
  shouldPreventDefault,
  shouldStopPropagation,
  shouldRemovePadding,
  checked: checkedProp,
  defaultChecked,
  onCheckedChange,
  onClick,
  prefix,
  children,
  suffix,
  ...rest
}) => {
  const [checked = false, setChecked] = useControllableState({
    prop: checkedProp,
    defaultProp: defaultChecked,
    onChange: onCheckedChange,
  });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (shouldPreventDefault) e.preventDefault();
    if (shouldStopPropagation) e.stopPropagation();
    if (onClick) onClick(e);
    setChecked(!checked);
  };

  return (
    <div className={styles.switchButtonWrapper}>
      <Button
        onClick={handleClick}
        type={htmlType}
        className={cn(className, {
          [styles.noPadding]: shouldRemovePadding,
        })}
        prefix={render(prefix, checked)}
        suffix={render(prefix, checked)}
        {...rest}
      >
        {render(children, checked)}
      </Button>
    </div>
  );
};

export { SwitchButton };
